import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import {
  LANDING_THEMES,
  LandingThemeId,
} from './landing-themes.config';
import { LandingThemeService } from './landing-theme.service';
import { LANDING_THEME_PREVIEW } from '../utils/di.tokens';

@Component({
  selector: 'lib-theme-switcher',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible) {
      @if (open()) {
        <div
          class="lp-theme-backdrop"
          aria-hidden="true"
          (click)="close()"
        ></div>
      }

      <div
        class="lp-theme-dock"
        [class.lp-theme-dock--open]="open()"
      >
        <div class="lp-theme-dock__rail">
          <button
            type="button"
            class="lp-theme-dock__toggle"
            [attr.aria-expanded]="open()"
            aria-controls="lp-theme-switcher-panel"
            [attr.aria-label]="open() ? 'Stillarni yopish' : 'Stillarni ochish'"
            (click)="toggle()"
          >
            <i class="pi pi-palette" aria-hidden="true"></i>
          </button>

          <div class="lp-theme-dock__rail-list">
            @for (theme of themes; track theme.id) {
              <div class="lp-theme-dock__rail-row">
                <button
                  type="button"
                  class="lp-theme-dock__rail-swatch"
                  [class.lp-theme-dock__rail-swatch--active]="
                    themeService.activeTheme() === theme.id
                  "
                  [style.background]="theme.swatch"
                  [attr.aria-label]="theme.label"
                  [attr.aria-pressed]="themeService.activeTheme() === theme.id"
                  (click)="pick(theme.id)"
                ></button>
              </div>
            }
          </div>
        </div>

        <aside
          id="lp-theme-switcher-panel"
          class="lp-theme-dock__panel"
          role="dialog"
          aria-modal="true"
          aria-label="Stillarni ko'rish"
          [attr.aria-hidden]="!open()"
        >
          <div class="lp-theme-dock__head">
            <p class="lp-theme-dock__title">Stillar</p>
            <button
              type="button"
              class="lp-theme-dock__close"
              aria-label="Yopish"
              (click)="close()"
            >
              <i class="pi pi-times" aria-hidden="true"></i>
            </button>
          </div>

          <div class="lp-theme-dock__list">
            @for (theme of themes; track theme.id) {
              <button
                type="button"
                class="lp-theme-dock__row"
                [class.lp-theme-dock__row--active]="
                  themeService.activeTheme() === theme.id
                "
                [attr.aria-pressed]="themeService.activeTheme() === theme.id"
                (click)="pick(theme.id)"
              >
                {{ theme.label }}
              </button>
            }
          </div>
        </aside>
      </div>
    }
  `,
  styles: `
    .lp-theme-backdrop {
      position: fixed;
      inset: 0;
      z-index: 198;
      background: rgb(15 23 42 / 0.08);
      animation: lp-theme-fade-in 0.25s ease;
    }

    @keyframes lp-theme-fade-in {
      from {
        opacity: 0;
      }

      to {
        opacity: 1;
      }
    }

    .lp-theme-dock {
      --lp-theme-rail-width: 2rem;
      --lp-theme-panel-width: 12.5rem;
      --lp-theme-row-height: 2.25rem;

      position: fixed;
      top: 50%;
      right: 0;
      z-index: 199;
      display: flex;
      align-items: flex-start;
      transform: translateY(-50%)
        translateX(calc(var(--lp-theme-panel-width)));
      transition: transform 0.38s cubic-bezier(0.22, 1, 0.36, 1);
      will-change: transform;
    }

    .lp-theme-dock--open {
      transform: translateY(-50%) translateX(0);
    }

    .lp-theme-dock__rail {
      display: flex;
      flex-direction: column;
      width: var(--lp-theme-rail-width);
      flex-shrink: 0;
      border: 1px solid rgb(15 23 42 / 0.08);
      border-right: none;
      border-radius: 0.5rem 0 0 0.5rem;
      background: rgb(255 255 255 / 0.98);
      backdrop-filter: blur(14px);
      box-shadow: -4px 0 20px rgb(15 23 42 / 0.1);
      overflow: hidden;
    }

    .lp-theme-dock__toggle {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: var(--lp-theme-row-height);
      padding: 0;
      border: none;
      border-bottom: 1px solid #f1f5f9;
      background: transparent;
      color: #334155;
      cursor: pointer;
      flex-shrink: 0;
      transition:
        background 0.2s ease,
        border-color 0.2s ease;

      .pi {
        font-size: 0.875rem;
        color: #ec4899;
        transition: transform 0.25s ease;
      }

      &:hover {
        background: #fdf2f8;

        .pi {
          transform: scale(1.08);
        }
      }
    }

    .lp-theme-dock--open .lp-theme-dock__toggle {
      border-bottom-color: #f1f5f9;
      background: #fdf2f8;
    }

    .lp-theme-dock__rail-list {
      display: flex;
      flex-direction: column;
      gap: 0.3rem;
      padding: 0.25rem 0.25rem 0.375rem;
    }

    .lp-theme-dock__rail-row {
      display: flex;
      align-items: center;
      justify-content: center;
      height: var(--lp-theme-row-height);
    }

    .lp-theme-dock__rail-swatch {
      width: 1.25rem;
      height: 1.25rem;
      padding: 0;
      border: 2px solid transparent;
      border-radius: 0.375rem;
      cursor: pointer;
      box-shadow: inset 0 0 0 1px rgb(0 0 0 / 0.08);
      transition:
        transform 0.15s ease,
        border-color 0.15s ease,
        box-shadow 0.15s ease;

      &:hover {
        transform: scale(1.1);
      }
    }

    .lp-theme-dock__rail-swatch--active {
      border-color: #fff;
      box-shadow:
        0 0 0 2px #f472b6,
        inset 0 0 0 1px rgb(0 0 0 / 0.06);
    }

    .lp-theme-dock__panel {
      display: flex;
      flex-direction: column;
      width: var(--lp-theme-panel-width);
      max-height: min(
        calc(var(--lp-theme-row-height) * 6 + 2.5rem),
        calc(100vh - 3rem)
      );
      flex-shrink: 0;
      border: 1px solid rgb(15 23 42 / 0.08);
      border-right: none;
      background: rgb(255 255 255 / 0.98);
      backdrop-filter: blur(14px);
      box-shadow: -10px 0 36px rgb(15 23 42 / 0.12);
      visibility: hidden;
      pointer-events: none;
      opacity: 0;
      transition:
        opacity 0.22s ease 0.08s,
        visibility 0.22s ease 0.08s;
    }

    .lp-theme-dock--open .lp-theme-dock__panel {
      visibility: visible;
      pointer-events: auto;
      opacity: 1;
    }

    .lp-theme-dock__head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.375rem;
      height: var(--lp-theme-row-height);
      padding-inline: 0.625rem;
      border-bottom: 1px solid #f1f5f9;
      flex-shrink: 0;
    }

    .lp-theme-dock__title {
      margin: 0;
      font-size: 0.625rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #94a3b8;
    }

    .lp-theme-dock__close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      border: none;
      border-radius: 0.375rem;
      background: transparent;
      color: #94a3b8;
      font-size: 0.75rem;
      cursor: pointer;
      transition:
        background 0.15s ease,
        color 0.15s ease;

      &:hover {
        background: #f1f5f9;
        color: #475569;
      }
    }

    .lp-theme-dock__list {
      display: flex;
      flex-direction: column;
      gap: 0.3rem;
      padding: 0.25rem 0.5rem 0.375rem;
      overflow-y: auto;
    }

    .lp-theme-dock__row {
      display: flex;
      align-items: center;
      width: 100%;
      height: var(--lp-theme-row-height);
      padding-inline: 0.5rem;
      border: 1px solid #e8edf3;
      border-radius: 0.625rem;
      background: #fff;
      font-size: 0.8125rem;
      font-weight: 600;
      line-height: 1.2;
      color: #1e293b;
      text-align: left;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      cursor: pointer;
      transition:
        border-color 0.15s ease,
        background 0.15s ease,
        box-shadow 0.15s ease;

      &:hover {
        border-color: #fbcfe8;
        background: #fdf2f8;
      }
    }

    .lp-theme-dock__row--active {
      border-color: #f472b6;
      background: #fdf2f8;
      box-shadow: 0 0 0 1px rgb(236 72 153 / 0.2);
    }

    @media (max-width: 380px) {
      .lp-theme-dock {
        --lp-theme-panel-width: calc(
          100vw - var(--lp-theme-rail-width) - 0.5rem
        );
      }
    }
  `,
})
export class ThemeSwitcherComponent {
  readonly themeService = inject(LandingThemeService);
  readonly themes = LANDING_THEMES;
  readonly visible = inject(LANDING_THEME_PREVIEW, { optional: true }) ?? false;
  readonly open = signal(false);

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.open()) {
      this.close();
    }
  }

  toggle(): void {
    this.open.update((value) => !value);
  }

  close(): void {
    this.open.set(false);
  }

  pick(themeId: LandingThemeId): void {
    this.themeService.setTheme(themeId);
  }
}
